import React, {
	useState,
	useEffect,
	useRef,
	useMemo,
	useCallback,
} from "react";
import BannerSlider from "../../components/BannerSlider";
import Notification from "../../components/Notification";
import { useLocation, useNavigate } from "react-router-dom";
import Load from "../../assets/load.gif";
import {
	getGameDataOnly,
	getGameDataDiscount,
	getGameDataNew,
	getGameDataPopular,
	getGame,
	getBanners,
} from "../../router/apiServiceFetch";
import GameSlider from "../../components/GameSlider";
import { useItemContext } from "../../contexts/ItemContext";
import { useAuth } from "../../contexts/AuthProvider";
import { useTelegram } from "../../hooks/useTelegram";
import GameBannerSlider from "../../components/GameBannerSlider";
import AllItems from "../../components/AllItems/AllItems";
import Games from "../../components/Games/Games";
import right from "../../assets/right.svg";
import "./Home.scss";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import BannerPage from "../../components/BannerPage/BannerPage";
import Item from "../../components/Item/Item";

const HOME_START_PARAM_HANDLED_KEY = "home_start_param_handled";

const Home = ({ onLoadingComplete }) => {
	const location = useLocation();
	const navigate = useNavigate();
	const { tg, triggerHapticFeedback } = useTelegram();
	const { userBase } = useAuth();
	const { handleAddToCart, handleRemoveFromCart, cartItems } = useItemContext();

	const autoOpenedRef = useRef(false);

	const queryParams = useMemo(
		() => new URLSearchParams(location.search),
		[location.search],
	);

	const startParam = useMemo(() => {
		return (
			queryParams.get("start_param") ||
			queryParams.get("tgWebAppStartParam") ||
			tg?.initDataUnsafe?.start_param ||
			""
		);
	}, [queryParams, tg]);

	const [selectedItem, setSelectedItem] = useState(null);
	const [allItemsPopup, setAllItemsPopup] = useState(false);
	const [errorText, setErrorText] = useState();
	const [showErrorPopup, setShowErrorPopup] = useState();
	const [isSuccess, setIsSuccess] = useState(true);
	const [itemsPopup, setItemsPopup] = useState(false);
	const [bannerPopup, setBannerPopup] = useState(false);
	const [selectedBanner, setSelectedBanner] = useState();
	const [popupTitle, setPopupTitle] = useState("");
	const [gamesItems, setGamesItems] = useState([]);
	const [games, setGames] = useState([]);
	const [banners, setBanners] = useState([]);
	const [gamesDiscount, setGamesDiscount] = useState([]);
	const [gamesNew, setGamesNew] = useState([]);
	const [gamesPopular, setGamesPopular] = useState([]);
	const [allItems, setAllItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [bannerLoading, setBannerLoading] = useState(true);
	const [loadingNew, setLoadingNew] = useState(true);
	const [loadingDiscount, setLoadingDiscount] = useState(true);
	const [loadingPopular, setLoadingPopular] = useState(true);
	const [type, setType] = useState();

	const cleanStartParamsFromUrl = useCallback(() => {
		const params = new URLSearchParams(location.search);
		let changed = false;

		if (params.has("start_param")) {
			params.delete("start_param");
			changed = true;
		}

		if (params.has("tgWebAppStartParam")) {
			params.delete("tgWebAppStartParam");
			changed = true;
		}

		if (!changed) return;

		const nextSearch = params.toString();

		navigate(
			{
				pathname: location.pathname,
				search: nextSearch ? `?${nextSearch}` : "",
			},
			{ replace: true },
		);
	}, [location.pathname, location.search, navigate]);

	const fetchGamesOnly = async () => {
		try {
			setLoading(true);
			const fetchedGames = await getGameDataOnly();

			setGames((prev) =>
				JSON.stringify(fetchedGames) !== JSON.stringify(prev)
					? fetchedGames
					: prev,
			);
		} catch (error) {
			console.error("Error fetching games data:", error);
		} finally {
			setLoading(false);
		}
	};

	const fetchBanners = async () => {
		try {
			setBannerLoading(true);
			const bannersData = await getBanners();

			const processedBanners =
				bannersData.length === 1
					? [...bannersData, { ...bannersData[0] }]
					: bannersData;

			setBanners((prev) =>
				JSON.stringify(processedBanners) !== JSON.stringify(prev)
					? processedBanners
					: prev,
			);
		} catch (error) {
			console.error("Error fetching banners data:", error);
		} finally {
			setBannerLoading(false);
		}
	};

	const fetchGamesDiscount = async () => {
		if (!userBase?.userId) return;

		try {
			setLoadingDiscount(true);
			const fetchedGames = await getGameDataDiscount(userBase.userId);
			const processedGames = fetchedGames
				.map((game) => ({
					name: game.name,
					gameId: game.gameId,
					parentFileName: game.fileName,
					items: [...game.gameGroups.flatMap((group) => group.items)],
				}))
				.filter((group) => group.items.length > 0);

			setGamesDiscount((prev) =>
				JSON.stringify(processedGames) !== JSON.stringify(prev)
					? processedGames
					: prev,
			);
		} catch (error) {
			console.error("Error fetching games data:", error);
		} finally {
			setLoadingDiscount(false);
		}
	};

	const fetchGamesNew = async () => {
		if (!userBase?.userId) return;

		try {
			setLoadingNew(true);
			const fetchedGames = await getGameDataNew(userBase.userId);
			const processedGames = fetchedGames
				.map((game) => ({
					name: game.name,
					gameId: game.gameId,
					parentFileName: game.fileName,
					items: [...game.gameGroups.flatMap((group) => group.items)],
				}))
				.filter((group) => group.items.length > 0);

			setGamesNew((prev) =>
				JSON.stringify(processedGames) !== JSON.stringify(prev)
					? processedGames
					: prev,
			);
		} catch (error) {
			console.error("Error fetching games data:", error);
		} finally {
			setLoadingNew(false);
		}
	};

	const fetchGamesPopular = async () => {
		if (!userBase?.userId) return;

		try {
			setLoadingPopular(true);
			const fetchedGames = await getGameDataPopular(userBase.userId);
			const processedGames = fetchedGames
				.map((game) => ({
					name: game.name,
					gameId: game.gameId,
					parentFileName: game.fileName,
					items: [...game.gameGroups.flatMap((group) => group.items)],
				}))
				.filter((group) => group.items.length > 0);

			setGamesPopular((prev) =>
				JSON.stringify(processedGames) !== JSON.stringify(prev)
					? processedGames
					: prev,
			);
		} catch (error) {
			console.error("Error fetching games data:", error);
		} finally {
			setLoadingPopular(false);
		}
	};

	const handleAllItems = (title, type, gameData) => {
		triggerHapticFeedback?.("impact", "medium");
		setType(type);
		setPopupTitle(title);
		setAllItems(
			type === 1
				? gamesPopular
				: type === 2
					? gamesDiscount
					: type === 3
						? gamesNew
						: type === 5
							? gameData
							: [],
		);
		setAllItemsPopup(!allItemsPopup);
	};

	const handleItems = (title) => {
		if (title !== "") {
			triggerHapticFeedback?.("impact", "medium");
		}
		setPopupTitle(title);
		setAllItems(games);
		setItemsPopup(!itemsPopup);
	};

	const handleBanner = useCallback(
		(banner) => {
			triggerHapticFeedback?.("impact", "medium");
			setSelectedBanner(banner);
			setBannerPopup(true);
		},
		[triggerHapticFeedback],
	);

	const handleBannerClose = () => {
		triggerHapticFeedback?.("impact", "medium");
		setBannerPopup(false);
	};

	const handleGameClick = async (banner) => {
		let gameData = [];
		try {
			const fetchedGame = await getGame(banner.gameId, userBase.userId);

			if (!fetchedGame || fetchedGame.length === 0) {
				setGamesItems(gameData);
				return;
			}

			gameData = [
				{
					name: fetchedGame.name,
					gameGroups: fetchedGame.gameGroups.map((group) => ({
						groupName: group.name,
						groupId: group.groupId,
						items: group.items,
					})),
				},
			];

			setGamesItems(gameData);
		} catch (error) {
			console.error("Error fetching game data:", error);
			setGamesItems(gameData);
		}
		handleAllItems(banner.name, 5, gameData);
	};

	const handleGameItemClick = (item) => {
		triggerHapticFeedback?.("impact", "medium");

		setSelectedItem(item);
	};
	const handleItemClose = () => {
		setSelectedItem(null);
	};

	useEffect(() => {
		fetchGamesOnly();
		fetchBanners();
	}, []);

	useEffect(() => {
		if (!userBase?.userId) return;

		fetchGamesDiscount();
		fetchGamesNew();
		fetchGamesPopular();
	}, [userBase?.userId]);

	useEffect(() => {
		if (
			!loading &&
			!bannerLoading &&
			!loadingDiscount &&
			!loadingNew &&
			!loadingPopular
		) {
			onLoadingComplete?.();
		}
	}, [
		loading,
		bannerLoading,
		loadingDiscount,
		loadingNew,
		loadingPopular,
		onLoadingComplete,
	]);

	useEffect(() => {
		if (autoOpenedRef.current) return;
		if (!banners?.length) return;

		const alreadyHandled =
			sessionStorage.getItem(HOME_START_PARAM_HANDLED_KEY) === "1";

		if (alreadyHandled) {
			cleanStartParamsFromUrl();
			return;
		}

		const targetServiceName = startParam ? startParam.trim().toLowerCase() : null;

		if (!targetServiceName) return;

		const targetBanner = banners.find(
			(x) => (x.serviceName || "").trim().toLowerCase() === targetServiceName,
		);

		sessionStorage.setItem(HOME_START_PARAM_HANDLED_KEY, "1");
		autoOpenedRef.current = true;

		if (targetBanner) {
			handleBanner(targetBanner);
		}

		cleanStartParamsFromUrl();
	}, [startParam, banners, handleBanner, cleanStartParamsFromUrl]);

	return (
		<div className="home">
			<Notification />
			{allItemsPopup && (
				<AllItems
					allItems={allItems}
					type={type}
					onClose={() => handleAllItems("")}
					popupTitle={popupTitle}
					cartItems={cartItems}
					handleAddToCart={handleAddToCart}
					handleRemoveFromCart={handleRemoveFromCart}
				/>
			)}
			{itemsPopup && (
				<Games
					items={allItems}
					type={type}
					onClose={() => handleItems("")}
					popupTitle={popupTitle}
					cartItems={cartItems}
					handleAddToCart={handleAddToCart}
					handleRemoveFromCart={handleRemoveFromCart}
				/>
			)}
			{bannerPopup && (
				<BannerPage
					selectedBanner={selectedBanner}
					onClose={() => handleBannerClose()}
				/>
			)}
			{selectedItem ? (
				<div className="item-div">
					<Item gameItem={selectedItem} onClose={handleItemClose} />
				</div>
			) : (
				<div className="home-content">
					<BannerSlider banners={banners} handleBanner={handleBanner} />
					<div className="box">
						<div className="box-nav">
							<div className="box-nav-border">
								<div className="border" />
								<h2 onClick={() => handleItems("ИГРЫ")}>Игры</h2>
							</div>
							{!loading && (
								<div
									className="box-nav-btn"
									onClick={() => handleItems("ИГРЫ")}
								>
									<button>
										ещё
										<img src={right} alt="right" />
									</button>
								</div>
							)}
						</div>
						<div className="box-content">
							<div className="box-content-inner">
								{loading ? (
									<div className="loading-gif">
										<img src={Load} alt="load" className="load" />
										<img src={Load} alt="load" className="load" />
										<img src={Load} alt="load" className="load" />
									</div>
								) : (
									<GameBannerSlider
										banners={games}
										onClick={handleGameClick}
										cartItems={cartItems}
										handleAddToCart={handleAddToCart}
										handleRemoveFromCart={handleRemoveFromCart}
									/>
								)}
							</div>
						</div>
					</div>

					<div className="box-game">
						<div className="box-nav">
							<div className="box-nav-border">
								<div className="border" />
								<h2 onClick={() => handleAllItems("Популярные", 1)}>
									Популярные
								</h2>
							</div>
							{!loadingPopular &&
								gamesPopular &&
								gamesPopular.length > 0 && (
									<div
										className="box-nav-btn"
										onClick={() => handleAllItems("Популярные", 1)}
									>
										<button>
											ещё
											<img src={right} alt="right" />
										</button>
									</div>
								)}
						</div>
						<div className="box-games">
							{loadingPopular ? (
								<div className="loading-gif">
									<img src={Load} alt="load" className="load" />
									<img src={Load} alt="load" className="load" />
									<img src={Load} alt="load" className="load" />
								</div>
							) : gamesPopular && gamesPopular.length > 0 ? (
								<GameSlider
									banners={gamesPopular}
									cartItems={cartItems}
									handleAddToCart={handleAddToCart}
									handleRemoveFromCart={handleRemoveFromCart}
									handleGameItemClick={handleGameItemClick}
								/>
							) : (
								<p>Нет популярных</p>
							)}
						</div>
					</div>

					<div className="box-game">
						<div className="box-nav">
							<div className="box-nav-border">
								<div className="border" />
								<h2 onClick={() => handleAllItems("Новинки", 3)}>Новинки</h2>
							</div>
							{!loadingNew && gamesNew && gamesNew.length > 0 && (
								<div
									className="box-nav-btn"
									onClick={() => handleAllItems("Новинки", 3)}
								>
									<button>
										ещё
										<img src={right} alt="right" />
									</button>
								</div>
							)}
						</div>
						<div className="box-games">
							{loadingNew ? (
								<div className="loading-gif">
									<img src={Load} alt="load" className="load" />
									<img src={Load} alt="load" className="load" />
									<img src={Load} alt="load" className="load" />
								</div>
							) : gamesNew && gamesNew.length > 0 ? (
								<GameSlider
									banners={gamesNew}
									cartItems={cartItems}
									handleAddToCart={handleAddToCart}
									handleRemoveFromCart={handleRemoveFromCart}
									handleGameItemClick={handleGameItemClick}
								/>
							) : (
								<p>Новинок нет</p>
							)}
						</div>
					</div>

					<div className="box-game">
						<div className="box-nav">
							<div className="box-nav-border">
								<div className="border" />
								<h2 onClick={() => handleAllItems("Скидки", 2)}>Скидки</h2>
							</div>
							{!loadingDiscount &&
								gamesDiscount &&
								gamesDiscount.length > 0 && (
									<div
										className="box-nav-btn"
										onClick={() => handleAllItems("Скидки", 2)}
									>
										<button>
											ещё
											<img src={right} alt="right" />
										</button>
									</div>
								)}
						</div>
						<div className="box-games">
							{loadingDiscount ? (
								<div className="loading-gif">
									<img src={Load} alt="load" className="load" />
									<img src={Load} alt="load" className="load" />
									<img src={Load} alt="load" className="load" />
								</div>
							) : gamesDiscount && gamesDiscount.length > 0 ? (
								<GameSlider
									banners={gamesDiscount}
									cartItems={cartItems}
									handleAddToCart={handleAddToCart}
									handleRemoveFromCart={handleRemoveFromCart}
									handleGameItemClick={handleGameItemClick}
								/>
							) : (
								<p>Скидок нет</p>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default Home;
